import { getCountries, getCountryCallingCode, CountryCode } from 'libphonenumber-js';
// Import labels using the package export path
import labels from 'react-phone-number-input/locale/en.json';

export interface CountryData {
  code: CountryCode;
  name: string;
  dialCode: string;
  flag: string; // Mantido para compatibilidade, mas não é usado
}

/**
 * Retorna a lista completa de países ordenados conforme especificação:
 * 1. Canadá (sempre primeiro)
 * 2. Países comuns (US, UK, BR, AU)
 * 3. Resto dos países ordenados alfabeticamente
 */
export function getOrderedCountries(): CountryData[] {
  const allCountries = getCountries();
  
  const commonCountries: CountryCode[] = ['CA', 'US', 'GB', 'BR', 'AU'];
  const otherCountries = allCountries.filter(code => !commonCountries.includes(code));
  
  // Ordena outros países alfabeticamente pelo nome
  otherCountries.sort((a, b) => {
    const nameA = labels[a as keyof typeof labels] || '';
    const nameB = labels[b as keyof typeof labels] || '';
    return nameA.localeCompare(nameB);
  });
  
  // Constrói array final: CA, depois US/GB/BR/AU ordenados, depois o resto
  const orderedCodes: CountryCode[] = [
    'CA', // Canadá sempre primeiro
    ...commonCountries.filter(code => code !== 'CA').sort((a, b) => {
      const nameA = labels[a as keyof typeof labels] || '';
      const nameB = labels[b as keyof typeof labels] || '';
      return nameA.localeCompare(nameB);
    }),
    ...otherCountries
  ];
  
  return orderedCodes.map(code => ({
    code,
    name: labels[code as keyof typeof labels] || code,
    dialCode: `+${getCountryCallingCode(code)}`,
    flag: '' // Não usado mais, mantido para compatibilidade
  }));
}

/**
 * Retorna o país pelo código de discagem (dial code)
 * Se houver múltiplos países com o mesmo dialCode, tenta retornar o país memorizado no localStorage
 */
export function getCountryByDialCode(dialCode: string, preferredCountryCode?: string): CountryData | undefined {
  const countries = getOrderedCountries();
  const countriesWithDialCode = countries.filter(country => country.dialCode === dialCode);
  
  if (countriesWithDialCode.length === 0) {
    return undefined;
  }
  
  // Se há apenas um país com esse dialCode, retorna ele
  if (countriesWithDialCode.length === 1) {
    return countriesWithDialCode[0];
  }
  
  // Se há múltiplos países (ex: US e CA com +1), tenta usar o preferido
  if (preferredCountryCode) {
    const preferred = countriesWithDialCode.find(c => c.code === preferredCountryCode);
    if (preferred) {
      return preferred;
    }
  }
  
  // Tenta buscar do localStorage a última seleção do usuário para esse dialCode
  try {
    const storageKey = `lastSelectedCountry_${dialCode}`;
    const lastSelectedCode = localStorage.getItem(storageKey);
    if (lastSelectedCode) {
      const lastSelected = countriesWithDialCode.find(c => c.code === lastSelectedCode);
      if (lastSelected) {
        return lastSelected;
      }
    }
  } catch (error) {
    // Se localStorage não estiver disponível, ignora
  }
  
  // Se não encontrou preferência, retorna o primeiro da lista (que é Canadá para +1)
  return countriesWithDialCode[0];
}

/**
 * Retorna todos os países com um determinado código de discagem (dial code)
 */
export function getCountriesByDialCode(dialCode: string): CountryData[] {
  const countries = getOrderedCountries();
  return countries.filter(country => country.dialCode === dialCode);
}

/**
 * Retorna o país pelo código ISO
 */
export function getCountryByCode(code: CountryCode): CountryData | undefined {
  const countries = getOrderedCountries();
  return countries.find(country => country.code === code);
}

/**
 * Retorna países filtrados por termo de busca
 */
export function searchCountries(query: string): CountryData[] {
  const countries = getOrderedCountries();
  if (!query.trim()) return countries;
  
  const lowerQuery = query.toLowerCase();
  return countries.filter(country => 
    country.name.toLowerCase().includes(lowerQuery) ||
    country.dialCode.includes(query) ||
    country.code.toLowerCase().includes(lowerQuery)
  );
}
